import { z } from "zod";
import { USER_ROLES } from "@algoquest/shared";

/**
 * Phase 09 — the `auth`/`users` domains' request/response DTOs (Master
 * Spec §29, §30, §33). Concrete shapes were deliberately not guessed at
 * in Phase 00 (see `routes.ts`'s own doc comment) — they're added now,
 * alongside the Fastify handlers that actually use them, so game/web
 * clients and the API validate against one shared source of truth
 * instead of three independently-typed guesses.
 *
 * Token storage (Product Owner decision, Phase 09): the access token is
 * short-lived and returned in the JSON body for the client to hold in
 * memory only (never localStorage, per §30); the refresh token is
 * long-lived and is NEVER present in any JSON response body — it is set
 * by the API as an httpOnly, Secure, SameSite cookie scoped to
 * `/api/v1/auth/refresh`, so it is inaccessible to JavaScript on either
 * client and is only ever sent back automatically on that one endpoint.
 */

export const EmailSchema = z.string().trim().toLowerCase().email().max(320);

/**
 * Password length is the one policy lever enforced here (10-200 chars) —
 * following modern guidance (e.g. NIST 800-63B) that length matters far
 * more than forced complexity rules (a required digit/symbol/uppercase
 * mix pushes users toward predictable substitutions, not stronger
 * passwords), which the Master Prompt does not otherwise specify. Only
 * applied to registration — `LoginRequestSchema` below deliberately does
 * NOT re-validate an existing password against this policy, since a
 * policy change must never lock out an account created under an older,
 * looser policy.
 */
export const NewPasswordSchema = z.string().min(10).max(200);

export const UserRoleSchema = z.enum(USER_ROLES);
export type UserRoleDTO = z.infer<typeof UserRoleSchema>;

export const UserDTOSchema = z.object({
  id: z.string().uuid(),
  email: EmailSchema,
  displayName: z.string().min(1).max(80),
  roles: z.array(UserRoleSchema),
  schoolName: z.string().nullable().optional(),
  className: z.string().nullable().optional(),
});
export type UserDTO = z.infer<typeof UserDTOSchema>;

export const SchoolNameSchema = z.string().trim().min(1).max(160);
export const ClassNameSchema = z.string().trim().min(1).max(80);

export const RegisterRequestSchema = z.object({
  schoolName: SchoolNameSchema,
  className: ClassNameSchema,
  email: EmailSchema,
  password: NewPasswordSchema,
  displayName: z.string().trim().min(1).max(80),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const LoginRequestSchema = z.object({
  email: EmailSchema,
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Returned by `POST /api/v1/auth/register`, `POST /api/v1/auth/login`,
 * and `POST /api/v1/auth/refresh` alike — same shape whether a session
 * was just created or just renewed. `accessTokenExpiresAt` is an ISO 8601
 * timestamp so the client can schedule its own silent-refresh call
 * proactively instead of waiting for a 401.
 */
export const AuthResponseSchema = z.object({
  user: UserDTOSchema,
  accessToken: z.string().min(1),
  accessTokenExpiresAt: z.string().datetime(),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

export const LogoutResponseSchema = z.object({
  success: z.literal(true),
});
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

/** Uniform shape for every non-2xx `/api/v1` response (Master Spec §29). */
export const ApiErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;

export const ChangePasswordRequestSchema=z.object({currentPassword:z.string().min(1).max(200),newPassword:NewPasswordSchema}).strict();
export const ChangeEmailRequestSchema=z.object({currentPassword:z.string().min(1).max(200),newEmail:EmailSchema}).strict();
export const ForgotPasswordRequestSchema=z.object({email:EmailSchema}).strict();
export const ResetPasswordRequestSchema=z.object({token:z.string().regex(/^[a-f0-9]{64}$/),newPassword:NewPasswordSchema}).strict();
export const ConfirmEmailRequestSchema=z.object({token:z.string().regex(/^[a-f0-9]{64}$/)}).strict();
